import { Link, Stack, Typography } from '@mui/material';
import { ExternalLink } from 'lucide-react';
import { SettingsSection, SettingsSectionRow } from 'src/components/SettingsSection';
import { formatAmountCents } from 'src/utils/number';
import { humanize } from 'src/utils/strings';
import { SectionErrorRow, SectionLoadingRow } from './section-states';
import { StatusBadge, StatusBadgeTone } from './styled';
import { PartnerInvoice, usePartnerInvoices } from './use-partner-invoices';

const formatInvoiceDate = (createdUnix: number) =>
  new Date(createdUnix * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const getInvoiceStatusBadge = (status: string): StatusBadgeTone => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'open':
      return 'warning';
    case 'uncollectible':
      return 'error';
    default:
      return 'neutral';
  }
};

const InvoiceRowControl = ({ invoice }: { invoice: PartnerInvoice }) => (
  <Stack direction="row" alignItems="center" gap={2}>
    <StatusBadge status={getInvoiceStatusBadge(invoice.status)}>
      {humanize(invoice.status)}
    </StatusBadge>
    <Typography
      variant="body2"
      fontWeight={500}
      color="custom.OV.sys.onSurface"
      minWidth={(theme) => theme.typography.pxToRem(80)}
      textAlign="right"
    >
      {formatAmountCents(invoice.total, '—')}
    </Typography>
    {invoice.hostedInvoiceUrl ? (
      <Link
        href={invoice.hostedInvoiceUrl}
        target="_blank"
        rel="noopener noreferrer"
        variant="body2"
        underline="hover"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
      >
        View
        <ExternalLink size={14} />
      </Link>
    ) : null}
  </Stack>
);

export const PartnerInvoicesSection = () => {
  const { invoices, loading, error, refetch } = usePartnerInvoices();

  let body;
  if (loading) {
    body = <SectionLoadingRow />;
  } else if (error) {
    body = (
      <SectionErrorRow
        message="We couldn't load your invoices. Please try again."
        onRetry={() => void refetch()}
      />
    );
  } else if (invoices.length === 0) {
    body = <SettingsSectionRow title="No invoices to display." />;
  } else {
    body = invoices.map((invoice) => (
      <SettingsSectionRow
        key={invoice.id}
        title={formatInvoiceDate(invoice.created)}
        secondary={invoice.number ?? undefined}
        control={<InvoiceRowControl invoice={invoice} />}
        controlAlign="center"
        mobileControlLayout="below"
      />
    ));
  }

  return (
    <SettingsSection
      title="Invoices"
      description="Invoices billed to you by OneVision, newest first."
    >
      {body}
    </SettingsSection>
  );
};
